/**
 * The Corporation screen (M6 plan P33, spec §8): treasury, the P&L, wallet ↔ company transfers, the claims held (with
 * a link to the Charter Board), the milestones, the logo and the livery. Shown in the Registration tab at the Charter
 * Hub once the corporation is founded; its Stations button opens the station pages (P34). Thin: the rules are `corpModel.ts`.
 * M7 S39: after the first finale commit, the tetherstone ballot toggle (`ballotModel.ts`): one focusable row per
 * measure; a press cycles the stance.
 */
import type { ReactNode } from 'react'
import type { CorpView, LiveryColors, MeasureName } from '../../strategic/types'
import { ballotScreenConfig, type BallotRow } from './ballotModel'
import { FocusButton } from '../focus'
import { keepTyping } from './typing'
import { formatCr } from '../format'
import { milestoneRows, pnlModel, transferBlock, TRANSFER_STEPS, type PnlRow, type TransferDirection } from './corpModel'
import { LiveryPicker, liveryPickerSize } from './LiveryPicker'
import { LogoMark } from './LogoEditor'
import { dockingLabel, tariffLabel } from './stationsModel'

export interface CorporationProps {
  view: CorpView
  wallet: number
  amount: number
  livery: LiveryColors
  busy: boolean
  status: string | null
  onAmount(amount: number): void
  onTransfer(direction: TransferDirection): void
  onLivery(livery: LiveryColors): void
  onApplyLivery(): void
  onOpenBoard(): void
  /** Opens the Stations pages (M6 P34). */
  onStations(): void
  /** A system id in words. */
  systemName(id: string): string
  /** M7 S39: the tetherstone ballot rows (absent or null: hidden) and the press that cycles one. */
  ballot?: { rows: readonly BallotRow[]; onCycle(measure: MeasureName): void } | null
}

function Ballot(p: { rows: readonly BallotRow[]; first: number; busy: boolean; onCycle(measure: MeasureName): void }): ReactNode {
  const cfg = ballotScreenConfig()
  return (
    <section data-testid="corp-ballot">
      <h4 className="corp-sub">{cfg.heading}</h4>
      <p className="fp-meta">{cfg.intro}</p>
      {p.rows.map((r, k) => (
        <FocusButton
          key={r.measure}
          group="corp"
          index={p.first + k}
          className="ui-btn--small"
          testId={`corp-ballot-${r.measure}`}
          disabled={p.busy}
          onActivate={() => p.onCycle(r.measure)}
        >
          {r.label}: <span data-stance={r.stance}>{r.stanceLabel}</span>
        </FocusButton>
      ))}
    </section>
  )
}

function Row(p: { row: PnlRow; strong?: boolean }): ReactNode {
  const cls = p.strong === true ? 'corp-pnl__total' : undefined
  return (
    <tr className={cls} data-testid={`corp-pnl-${p.row.key}`}>
      <td>{p.row.label}</td>
      <td className="ui-num">{formatCr(p.row.yesterday)}</td>
      <td className="ui-num">{formatCr(p.row.today)}</td>
    </tr>
  )
}

export function Corporation(p: CorporationProps): ReactNode {
  const v = p.view
  const pnl = pnlModel(v)
  const toCompany = transferBlock(p.amount, 'toCompany', p.wallet, v.treasury)
  const toWallet = transferBlock(p.amount, 'toWallet', p.wallet, v.treasury)
  let i = 0
  return (
    <div className="sui fp-panel corp-panel" data-testid="corp-screen">
      <div className="corp-head">
        {v.logo !== null ? <LogoMark recipe={v.logo} size={64} testId="corp-screen-logo" /> : null}
        <div>
          <h3 className="corp-title" data-testid="corp-screen-name">
            {v.name} <span className="fp-row__sub">[{v.ticker}]</span>
          </h3>
          <p className="fp-meta">
            Founded on day {v.foundedDay ?? '–'} · Treasury{' '}
            <span className="ui-num" data-testid="corp-treasury">
              {formatCr(v.treasury)}
            </span>{' '}
            · Wallet <span className="ui-num">{formatCr(p.wallet)}</span> · Charter votes {v.voteWeight}
          </p>
        </div>
      </div>

      <FocusButton group="corp" index={i++} className="ui-btn--small" testId="corp-stations" onActivate={p.onStations}>
        Stations and claims…
      </FocusButton>

      <h4 className="corp-sub">Profit and loss</h4>
      <table className="corp-pnl" data-testid="corp-pnl">
        <thead>
          <tr>
            <th />
            <th>Yesterday</th>
            <th>Today</th>
          </tr>
        </thead>
        <tbody>
          {pnl.income.map((r) => (
            <Row key={`i-${r.key}`} row={r} />
          ))}
          <Row row={pnl.totals.income} strong />
          {pnl.costs.map((r) => (
            <Row key={`c-${r.key}`} row={r} />
          ))}
          <Row row={pnl.totals.costs} strong />
          <Row row={pnl.totals.net} strong />
        </tbody>
      </table>

      <h4 className="corp-sub">Transfers</h4>
      <div className="corp-transfer">
        <label className="corp-field">
          Amount
          <input
            type="number"
            min={0}
            step={100}
            value={p.amount}
            disabled={p.busy}
            data-testid="corp-amount"
            onKeyDown={keepTyping}
            onChange={(e) => p.onAmount(Number(e.target.value))}
          />
        </label>
        {TRANSFER_STEPS.map((s) => (
          <FocusButton
            key={s}
            group="corp"
            index={i++}
            className="ui-btn--small"
            testId={`corp-amount-${s}`}
            disabled={p.busy}
            onActivate={() => p.onAmount(s)}
          >
            {formatCr(s)}
          </FocusButton>
        ))}
        <FocusButton
          group="corp"
          index={i++}
          className="ui-btn--small"
          testId="corp-to-company"
          disabled={p.busy || toCompany !== ''}
          title={toCompany === '' ? undefined : toCompany}
          onActivate={() => p.onTransfer('toCompany')}
        >
          Wallet → company
        </FocusButton>
        <FocusButton
          group="corp"
          index={i++}
          className="ui-btn--small"
          testId="corp-to-wallet"
          disabled={p.busy || toWallet !== ''}
          title={toWallet === '' ? undefined : toWallet}
          onActivate={() => p.onTransfer('toWallet')}
        >
          Company → wallet
        </FocusButton>
      </div>

      <h4 className="corp-sub">Claims</h4>
      {v.claims.length === 0 ? (
        <p className="sui-empty" data-testid="corp-claims-empty">
          No claims held yet. Contribute to a system&apos;s Charter score to claim it.
        </p>
      ) : (
        <ul className="corp-list" data-testid="corp-claims">
          {v.claims.map((c) => (
            <li key={c.systemId}>
              {p.systemName(c.systemId)} · score {c.score.toFixed(1)} · tariff {tariffLabel(c.tariff < 0 ? null : c.tariff)} · docking {dockingLabel(c.docking)}
            </li>
          ))}
        </ul>
      )}
      <FocusButton group="corp" index={i++} className="ui-btn--small" testId="corp-open-board" onActivate={p.onOpenBoard}>
        Open the Charter Board
      </FocusButton>

      <h4 className="corp-sub">Milestones</h4>
      <ul className="corp-list" data-testid="corp-milestones">
        {milestoneRows(v).map((m) => (
          <li key={m.id} className={m.day === null ? 'corp-milestone--open' : 'corp-milestone--done'}>
            {m.day === null ? '○' : '●'} {m.label}
            {m.day === null ? '' : ` (day ${m.day})`}
          </li>
        ))}
      </ul>

      <h4 className="corp-sub">Livery</h4>
      <LiveryPicker livery={p.livery} busy={p.busy} onChange={p.onLivery} first={i} />
      <FocusButton
        group="corp"
        index={i + liveryPickerSize()}
        className="ui-btn--small"
        testId="corp-apply-livery"
        disabled={p.busy}
        onActivate={p.onApplyLivery}
      >
        Repaint the fleet
      </FocusButton>
      {p.ballot == null ? null : <Ballot rows={p.ballot.rows} first={i + liveryPickerSize() + 1} busy={p.busy} onCycle={p.ballot.onCycle} />}
      {p.status !== null ? (
        <p className="fp-status" role="status" data-testid="corp-status">
          {p.status}
        </p>
      ) : null}
    </div>
  )
}
